import React, { useEffect, useState } from 'react';
import API from '../lib/api';
import { usePeriod } from '../contexts/PeriodContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { CheckCircle2, XCircle, RefreshCw, Users, AlertTriangle, Lock, TrendingUp } from 'lucide-react';

export default function PeriodProgressPage() {
  const { periods, activePeriod, setActivePeriod } = usePeriod();
  const [progress, setProgress] = useState([]);
  const [checklist, setChecklist] = useState(null);
  const [loading, setLoading]    = useState(true);
  const [selectedPid, setSelectedPid] = useState(activePeriod?.id || '');

  const load = async (pid) => {
    if (!pid) return;
    setLoading(true);
    try {
      const [pr, cl] = await Promise.all([
        API.get(`/periods/${pid}/progress`),
        API.get(`/periods/${pid}/validate-close`),
      ]);
      setProgress(pr.data);
      setChecklist(cl.data);
    } catch(e) { toast.error('Failed to load'); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    if (activePeriod?.id) setSelectedPid(activePeriod.id);
  }, [activePeriod?.id]);

  useEffect(() => { load(selectedPid); }, [selectedPid]);

  const closePeriod = async (force = false) => {
    const reason = force ? prompt('Enter override reason:') : null;
    if (force && !reason) return;
    try {
      await API.put(`/periods/${selectedPid}/status`, null, {
        params: { status: 'LOCKED', force, override_reason: reason }
      });
      toast.success('Period locked');
      load(selectedPid);
    } catch(e) {
      const detail = e.response?.data?.detail;
      if (typeof detail === 'object' && detail.failing_checks) {
        toast.error(`Cannot close: ${detail.failing_checks.join(', ')}`);
      } else {
        toast.error(e.response?.data?.detail || 'Close failed');
      }
    }
  };

  const totals = progress.reduce((acc, r) => ({
    total:     acc.total     + r.total,
    coded:     acc.coded     + r.coded,
    submitted: acc.submitted + r.submitted,
    approved:  acc.approved  + r.approved,
    pending:   acc.pending   + r.pending,
  }), { total: 0, coded: 0, submitted: 0, approved: 0, pending: 0 });

  return (
    <div className="p-6 space-y-5">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-xl font-bold">Period Progress</h1>
          <p className="text-sm text-slate-500">Reconciliation progress per cardholder and close checklist</p>
        </div>
        <Select value={selectedPid} onValueChange={setSelectedPid}>
          <SelectTrigger className="h-8 text-sm w-48"><SelectValue placeholder="Select period" /></SelectTrigger>
          <SelectContent className="bg-white">
            {periods.map(p => <SelectItem key={p.id} value={p.id}>{p.label} — {p.status}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><RefreshCw size={24} className="animate-spin text-slate-400" /></div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">

          {/* Close Checklist */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg border shadow-sm p-4">
              <h3 className="text-sm font-semibold mb-3 flex items-center gap-1.5">
                <Lock size={14} />
                Period Close Checklist
              </h3>
              {checklist ? (
                <div className="space-y-2">
                  {(checklist.checks || []).map(check => (
                    <div key={check.key} className={`flex items-start gap-2 p-2 rounded text-xs
                      ${check.passed ? 'bg-emerald-50 text-emerald-800' : 'bg-rose-50 text-rose-800'}`}>
                      {check.passed
                        ? <CheckCircle2 size={13} className="mt-0.5 text-emerald-600 flex-shrink-0" />
                        : <XCircle size={13} className="mt-0.5 text-rose-500 flex-shrink-0" />}
                      <div>
                        <div className="font-medium">{check.label}</div>
                        {check.detail && <div className="text-[10px] opacity-70 mt-0.5">{check.detail}</div>}
                      </div>
                    </div>
                  ))}

                  <div className="pt-2 space-y-2">
                    {checklist.can_close ? (
                      <Button size="sm" className="w-full bg-emerald-600 hover:bg-emerald-700"
                        onClick={() => closePeriod(false)}>
                        <Lock size={13} className="mr-1" /> Lock Period
                      </Button>
                    ) : (
                      <>
                        <div className="text-xs text-rose-600 text-center font-medium">
                          {checklist.pending} item(s) still require attention
                        </div>
                        <Button size="sm" variant="outline" className="w-full text-amber-700 border-amber-200"
                          onClick={() => closePeriod(true)}>
                          <AlertTriangle size={13} className="mr-1" /> Force Close (with reason)
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              ) : <p className="text-sm text-slate-400">Select a period</p>}
            </div>
          </div>

          {/* Per-cardholder progress */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-lg border shadow-sm">
              <div className="p-4 border-b flex items-center justify-between">
                <h3 className="text-sm font-semibold flex items-center gap-1.5">
                  <Users size={14} /> Cardholder Progress
                </h3>
                {totals.total > 0 && (
                  <span className="text-xs text-slate-500">
                    {totals.approved} of {totals.total} approved ({Math.round(totals.approved / totals.total * 100)}%)
                  </span>
                )}
              </div>
              <div className="overflow-x-auto">
                <table className="data-table" data-testid="period-progress-table">
                  <thead><tr>
                    <th>Cardholder</th><th>Email</th>
                    <th className="text-right">Total</th>
                    <th className="text-right">Coded</th>
                    <th className="text-right">Submitted</th>
                    <th className="text-right">Approved</th>
                    <th className="text-right">Pending</th>
                    <th>Progress</th>
                  </tr></thead>
                  <tbody>
                    {progress.length === 0 ? (
                      <tr><td colSpan={8} className="text-center py-8 text-slate-400">No transactions for this period</td></tr>
                    ) : progress.map((row, i) => (
                      <tr key={i} className={row.pending > 0 ? 'exception' : ''}>
                        <td className="font-medium">{row.cardholder_name}</td>
                        <td className="text-xs text-slate-500">{row.email}</td>
                        <td className="text-right tabular-nums">{row.total}</td>
                        <td className="text-right tabular-nums">
                          <span className={row.coded === row.total ? 'text-emerald-600 font-semibold' : ''}>{row.coded}</span>
                        </td>
                        <td className="text-right tabular-nums">{row.submitted}</td>
                        <td className="text-right tabular-nums">
                          <span className={row.approved === row.total ? 'text-emerald-600 font-semibold' : ''}>{row.approved}</span>
                        </td>
                        <td className="text-right tabular-nums">
                          {row.pending > 0
                            ? <span className="text-orange-600 font-semibold">{row.pending}</span>
                            : <span className="text-slate-300">0</span>}
                        </td>
                        <td className="min-w-24">
                          <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                            <div className="h-full bg-emerald-500 rounded-full"
                              style={{ width: `${row.approved_pct}%` }} />
                          </div>
                          <div className="text-[10px] text-slate-400 mt-0.5">{row.approved_pct}% approved</div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
